import type { ViewContainerRef } from '@angular/core';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { from, Subject } from 'rxjs';

import { LocalStorageService } from '@core/common-legacy';
import { OfferKind } from '@core/offers-types';
import type { TOfferKind } from '@core/offers-types';
import type { DialogConfigOptions } from '@core/user-interface-legacy';

import type { AppNavigatorRoute } from './navigation.types';
import { AppNavigatorRoot } from './navigation.types';

interface CandidateDialogParams {
  tab?: string;
  tabKey?: string;
  subtab?: string;
  messagesMode?: string;
  smsThread?: string;
  openInterview?: boolean;
  interviewOfferId?: number;
  interviewTalentPoolId?: number;
  interviewTemplateId?: number;
  interviewResultRequestId?: number;
  interviewEdit?: boolean;
  interviewResultId?: number;
  quickEvaluation?: boolean;
  fromHiddenEvaluations?: boolean;
  matchId?: number;
}

@Injectable({ providedIn: 'root' })
export class AppNavigatorService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly localStorage = inject(LocalStorageService);

  public readonly NAVIGATOR_HISTORY_CACHE_KEY: string = 'navigator_history_2';

  // TODO use routes instead
  public interviewScheduleDialogOpen$ = new Subject<{
    data: any;
    viewContainerRef?: any;
  }>();

  // TODO use routes instead
  public interviewEventDialogOpen$ = new Subject<{
    eventId: number;
    inCandidate?: boolean;
    config?: DialogConfigOptions;
    viewContainerRef?: any;
  }>();

  private navigatorHistory: Map<AppNavigatorRoot, AppNavigatorRoute> = new Map([
    [AppNavigatorRoot.Dashboard, { url: '/dashboard/overview' }],
    [AppNavigatorRoot.Mailbox, { url: '/mailbox/my/all' }],
  ]);

  constructor() {
    this.getNavigatorHistoryFromCache();
  }

  public getLastRoute(root: AppNavigatorRoot): AppNavigatorRoute | undefined {
    return this.navigatorHistory.has(root) ? this.navigatorHistory.get(root) : undefined;
  }

  public saveLastRoute(
    root: AppNavigatorRoot,
    route: AppNavigatorRoute,
    saveToCache: boolean = true,
  ): void {
    if (!route.url || route.url.indexOf(`/${root}/`) !== 0 || route.url === `/${root}/`) {
      throw new TypeError(`Invalid route.url: "${route.url}"`);
    }

    this.navigatorHistory.set(root, route);
    if (saveToCache) {
      this.saveNavigatorHistoryToCache();
    }
  }

  public clearLastRoute(root: AppNavigatorRoot): void {
    this.navigatorHistory.delete(root);
    this.saveNavigatorHistoryToCache();
  }

  public navigateToLastRoute(root: AppNavigatorRoot): Promise<boolean> {
    const { url, extras } = this.getLastRoute(root)!;
    return this.router.navigate([url], extras);
  }

  public getCandidateDialogRoute(candidateId: number, data: CandidateDialogParams = {}) {
    return this.router.createUrlTree([], {
      queryParams: {
        candidate: candidateId,
        tab: data.tab,
        tabKey: data.tabKey,
        subtab: data.subtab,
        messagesMode: data.messagesMode,
        smsThread: data.smsThread,
        openInterview: data.openInterview,
        interviewOfferId: data.interviewOfferId,
        interviewTalentPoolId: data.interviewTalentPoolId,
        interviewTemplateId: data.interviewTemplateId,
        interviewResultRequestId: data.interviewResultRequestId,
        interviewEdit: data.interviewEdit,
        interviewResultId: data.interviewResultId,
        quickEvaluation: data.quickEvaluation,
        fromHiddenEvaluations: data.fromHiddenEvaluations,
        matchId: data.matchId,
      },
      queryParamsHandling: 'merge',
    });
  }

  public openCandidateDialog(
    candidateId: number,
    data: CandidateDialogParams = {},
  ): Observable<boolean> {
    const { candidate } = this.route.snapshot.queryParams;
    const currentCandidateId = candidate ? parseInt(candidate, 10) : undefined;
    const open = this.router.navigateByUrl(this.getCandidateDialogRoute(candidateId, data), {
      replaceUrl: currentCandidateId === candidateId,
    });

    return from(open);
  }

  public navigateToOfferPage(offer: { kind: TOfferKind; slug: string }): void {
    let url = '';
    if (offer.kind === OfferKind.Job) {
      url = `/offers/${offer.slug}/pipeline`;
    } else {
      url = `/talent_pools/${offer.slug}/filters`;
    }
    this.router.navigate([url]);
  }

  public openInterviewScheduleDialog(event: {
    data: any;
    viewContainerRef?: ViewContainerRef;
  }): void {
    this.interviewScheduleDialogOpen$.next(event);
  }

  public openInterviewEventDialog(
    eventId: number,
    {
      inCandidate,
      config,
      viewContainerRef,
    }: { inCandidate?: boolean; config?: DialogConfigOptions; viewContainerRef?: ViewContainerRef },
  ): void {
    this.interviewEventDialogOpen$.next({ eventId, inCandidate, config, viewContainerRef });
  }

  private getNavigatorHistoryFromCache(): void {
    const serializedHistory = this.localStorage.get(this.NAVIGATOR_HISTORY_CACHE_KEY, {});
    Object.entries(serializedHistory).forEach(([key, value]: [string, { url: string }]) => {
      // Support for cache before changes on 27th Mar 2018
      if (
        value &&
        value.url &&
        'dashboard /dashboard /dashboard/ mailbox /mailbox /mailbox/'.split(' ').includes(value.url)
      ) {
        return;
      } else if (value && value.url && value.url[0] !== '/') {
        // eslint-disable-next-line no-param-reassign
        value = { ...value, url: `/${value.url}` };
      }

      this.navigatorHistory.set(<AppNavigatorRoot>key, value);
    });
  }

  private saveNavigatorHistoryToCache(): void {
    const serializedHistory = Array.from(this.navigatorHistory.entries()).reduce(
      (agg, redirectPair) => ({ ...agg, [redirectPair[0]]: redirectPair[1] }),
      {},
    );
    this.localStorage.set(this.NAVIGATOR_HISTORY_CACHE_KEY, serializedHistory);
  }
}
